import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
  TextField,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import { RRule } from "rrule";
import moment from "moment-timezone";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UploadIcon from "@mui/icons-material/Upload";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { API_URL } from "./config";

export default function ClassScheduleReport() {
  // ✅ Theme constants
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  const tableWrapSx = {
    ...surfaceSx,
    mt: 2,
    overflow: "hidden",
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)" },
  });

  const checkboxSx = {
    color: "rgba(25,118,210,0.75)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  // ===== Original state/logic =====
  const [campusCode, setCampusCode] = useState("");
  const [original, setOriginal] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);

  // filters
  const [fSub, setFSub] = useState([]);
  const [fDeg, setFDeg] = useState([]);
  const [fBr, setFBr] = useState([]);
  const [fSem, setFSem] = useState([]);
  const [fSubjCode, setFSubjCode] = useState([]);

  // lookup options (you had /api/filters/:campusCode)
  const [opts, setOpts] = useState({
    subs: [],
    degs: [],
    brs: [],
    sems: [],
    subjjs: [],
    teachers: [],
  });

  // edit state
  const [editId, setEditId] = useState(null);

  const token = localStorage.getItem("auth");

  // =========================
  // NEW: class keys for cascading dropdowns (Add + Bulk help)
  // =========================
  const [classKeys, setClassKeys] = useState([]);

  // =========================
  // NEW: Add Schedule form state
  // =========================
  const [addForm, setAddForm] = useState({
    SubCampusCode: "",
    DegreeCode: "",
    BranchCode: "",
    Semester: "",
    Regulation: "",
    SubjectCode: "",
    Section: "",
    // friendly schedule fields
    FromDate: moment().format("YYYY-MM-DD"),
    DayOfWeek: "MON",
    StartHHmm: "09:00",
    DurationMinutes: 60,
    UntilDate: "",
    IntervalWeeks: 1,
  });

  // NEW: Bulk upload
  //const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const fileInputRef = useRef(null);

  const downloadSampleExcel = () => {
  const headers = [
    "SubCampusCode",
    "DegreeCode",
    "BranchCode",
    "Semester",
    "Regulation",
    "SubjectCode",
    "Section",
    "FromDate",
    "DayOfWeek",
    "StartTime",
    "DurationMinutes",
    "UntilDate",
    "IntervalWeeks",
  ];

  const sampleRows = [
    {
      SubCampusCode: "SC01",
      DegreeCode: "BTECH",
      BranchCode: "CSE",
      Semester: "1",
      Regulation: "R23",
      SubjectCode: "CS101",
      Section: "A",
      FromDate: moment().format("YYYY-MM-DD"),
      DayOfWeek: "MON",
      StartTime: "09:00",
      DurationMinutes: 60,
      UntilDate: "",
      IntervalWeeks: 1,
    },
    {
      SubCampusCode: "SC01",
      DegreeCode: "BTECH",
      BranchCode: "CSE",
      Semester: "1",
      Regulation: "R23",
      SubjectCode: "CS101",
      Section: "B",
      FromDate: moment().format("YYYY-MM-DD"),
      DayOfWeek: "WED",
      StartTime: "10:00",
      DurationMinutes: 60,
      UntilDate: "",
      IntervalWeeks: 1,
    },
  ];

  const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
  ws["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));

  const instructions = [
    ["Field / Rule", "Instruction"],
    ["Sheet Name", "Keep sheet name exactly as: ClassSchedule"],
    ["Columns", "Do not rename/add/remove columns. Keep exact headers."],
    ["Keys", "SubCampusCode, DegreeCode, BranchCode, Semester, Regulation, SubjectCode, Section must match 'classes'."],
    ["FromDate", "YYYY-MM-DD (required). This is the starting date to align weekday from."],
    ["DayOfWeek", "Use MON/TUE/WED/THU/FRI/SAT/SUN (required)."],
    ["StartTime", "HH:mm 24-hour format (required). Example: 09:00, 14:30"],
    ["DurationMinutes", "Numeric (required). Example: 50, 60, 90"],
    ["UntilDate", "Optional YYYY-MM-DD. If blank, recurrence continues (or backend may treat as open-ended)."],
    ["IntervalWeeks", "Optional. Default 1. Use 2 for alternate weeks, etc."],
    ["Duplicates", "If backend has unique constraint, duplicate schedule rows will be rejected."],
  ];

  const ws2 = XLSX.utils.aoa_to_sheet(instructions);
  ws2["!cols"] = [{ wch: 22 }, { wch: 90 }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "ClassSchedule");
  XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `ClassSchedule_Sample_${campusCode || "Campus"}.xlsx`);
};



  // decode campusCode
  useEffect(() => {
    if (!token) return setLoading(false);
    try {
      const { campusCode: cc } = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(cc);
    } catch {
      setError("Invalid auth token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // fetch schedules + lookups + class keys
  useEffect(() => {
    if (!campusCode) return;
    setLoading(true);

    Promise.all([
      axios.get(`${API_URL}/api/classschedule`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_URL}/api/filters/${campusCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${API_URL}/api/classschedule/class-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([r1, r2, r3]) => {
        const mapped = (r1.data || []).map((item) => {
          let ropts = {};
          try {
            const rule = RRule.fromString(item.RecurrenceRule);
            ropts = rule.options || {};
          } catch {
            ropts = {};
          }

          const byweekday = Array.isArray(ropts.byweekday)
            ? ropts.byweekday
            : ropts.byweekday
            ? [ropts.byweekday]
            : [];

          return {
            ...item,
            RuleFreq: ropts.freq ? Object.keys(RRule).find((k) => RRule[k] === ropts.freq) : "WEEKLY",
            RuleInterval: ropts.interval || 1,
            RuleUntil: ropts.until ? moment(ropts.until).format("YYYY-MM-DD") : "",
            RuleWeekdays: byweekday.map((wd) => wd.toString()),
          };
        });

        setOriginal(mapped);
        setData(mapped);
        setOpts(r2.data);
        setClassKeys(r3.data || []);
      })
      .catch(() => setError("Failed to fetch data"))
      .finally(() => setLoading(false));
  }, [campusCode, success, token]);

  // client-side filter
  useEffect(() => {
    let tmp = original;
    if (fSub.length) tmp = tmp.filter((r) => fSub.includes(r.SubCampusCode));
    if (fDeg.length) tmp = tmp.filter((r) => fDeg.includes(r.DegreeCode));
    if (fBr.length) tmp = tmp.filter((r) => fBr.includes(r.BranchCode));
    if (fSem.length) tmp = tmp.filter((r) => fSem.includes(r.Semester));
    if (fSubjCode.length) tmp = tmp.filter((r) => fSubjCode.includes(r.SubjectNameCode));
    setData(tmp);
  }, [fSub, fDeg, fBr, fSem, fSubjCode, original]);

  // =========================
  // NEW: cascading dropdown option builders
  // =========================
  const addOptions = useMemo(() => {
    const k = classKeys;

    const subs = Array.from(new Set(k.map((x) => x.SubCampusCode))).filter(Boolean);

    const degs = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .map((x) => x.DegreeCode)
      )
    ).filter(Boolean);

    const brs = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .map((x) => x.BranchCode)
      )
    ).filter(Boolean);

    const sems = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .filter((x) => !addForm.BranchCode || x.BranchCode === addForm.BranchCode)
          .map((x) => x.Semester)
      )
    ).filter(Boolean);

    const regs = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .filter((x) => !addForm.BranchCode || x.BranchCode === addForm.BranchCode)
          .filter((x) => !addForm.Semester || x.Semester === addForm.Semester)
          .map((x) => x.Regulation)
      )
    ).filter(Boolean);

    /*
    const subjCodes = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .filter((x) => !addForm.BranchCode || x.BranchCode === addForm.BranchCode)
          .filter((x) => !addForm.Semester || x.Semester === addForm.Semester)
          .filter((x) => !addForm.Regulation || x.Regulation === addForm.Regulation)
          .map((x) => x.SubjectCode)
      )
    ).filter(Boolean);
    */

    const subjCodes = Array.from(
      new Map(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .filter((x) => !addForm.BranchCode || x.BranchCode === addForm.BranchCode)
          .filter((x) => !addForm.Semester || x.Semester === addForm.Semester)
          .filter((x) => !addForm.Regulation || x.Regulation === addForm.Regulation)
          .map((x) => [
            x.SubjectCode, // key
            {
              SubjectCode: x.SubjectCode,
              SubjectNameCode: x.SubjectNameCode, // can vary by section; usually same
              label: `${x.SubjectCode} - ${x.SubjectNameCode || ""}`.trim(),
            },
          ])
      ).values()
    );


    const sections = Array.from(
      new Set(
        k
          .filter((x) => !addForm.SubCampusCode || x.SubCampusCode === addForm.SubCampusCode)
          .filter((x) => !addForm.DegreeCode || x.DegreeCode === addForm.DegreeCode)
          .filter((x) => !addForm.BranchCode || x.BranchCode === addForm.BranchCode)
          .filter((x) => !addForm.Semester || x.Semester === addForm.Semester)
          .filter((x) => !addForm.Regulation || x.Regulation === addForm.Regulation)
          .filter((x) => !addForm.SubjectCode || x.SubjectCode === addForm.SubjectCode)
          .map((x) => x.Section)
      )
    ).filter(Boolean);

    const subjectName = (
      k.find(
        (x) =>
          x.SubCampusCode === addForm.SubCampusCode &&
          x.DegreeCode === addForm.DegreeCode &&
          x.BranchCode === addForm.BranchCode &&
          x.Semester === addForm.Semester &&
          x.Regulation === addForm.Regulation &&
          x.SubjectCode === addForm.SubjectCode &&
          x.Section === addForm.Section
      ) || {}
    ).SubjectNameCode;

    return { subs, degs, brs, sems, regs, subjCodes, sections, subjectName };
  }, [classKeys, addForm]);

  const setAdd = (patch) => setAddForm((s) => ({ ...s, ...patch }));

  // When parent selection changes, clear children
  const onPickSub = (v) =>
    setAdd({
      SubCampusCode: v || "",
      DegreeCode: "",
      BranchCode: "",
      Semester: "",
      Regulation: "",
      SubjectCode: "",
      Section: "",
    });
  const onPickDeg = (v) =>
    setAdd({
      DegreeCode: v || "",
      BranchCode: "",
      Semester: "",
      Regulation: "",
      SubjectCode: "",
      Section: "",
    });
  const onPickBr = (v) =>
    setAdd({
      BranchCode: v || "",
      Semester: "",
      Regulation: "",
      SubjectCode: "",
      Section: "",
    });
  const onPickSem = (v) =>
    setAdd({
      Semester: v || "",
      Regulation: "",
      SubjectCode: "",
      Section: "",
    });
  const onPickReg = (v) =>
    setAdd({
      Regulation: v || "",
      SubjectCode: "",
      Section: "",
    });
  const onPickSubj = (v) => setAdd({ SubjectCode: v || "", Section: "" });
  const onPickSection = (v) => setAdd({ Section: v || "" });

  // =========================
  // Existing table actions
  // =========================
  const handleInput = (e, rowId = null) => {
    const { name, value, checked, type } = e.target;
    const v = type === "checkbox" ? (checked ? 1 : 0) : value;
    if (rowId != null) {
      setData((ds) => ds.map((r) => (r.ScheduleID === rowId ? { ...r, [name]: v } : r)));
    }
  };

  const handleSave = (id) => {
    const row = data.find((r) => r.ScheduleID === id);
    const dtstart = row.StartTime ? new Date(row.StartTime) : new Date();

    let rruleOptions = {
      freq: RRule[row.RuleFreq] || RRule.WEEKLY,
      interval: parseInt(row.RuleInterval, 10) || 1,
      dtstart: new Date(
        Date.UTC(
          dtstart.getUTCFullYear(),
          dtstart.getUTCMonth(),
          dtstart.getUTCDate(),
          dtstart.getUTCHours(),
          dtstart.getUTCMinutes(),
          dtstart.getUTCSeconds()
        )
      ),
    };

    if (row.RuleUntil) {
      const [y, m, d] = row.RuleUntil.split("-").map(Number);
      rruleOptions.until = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
    }

    const rule = new RRule(rruleOptions);
    const rruleString = rule.toString();

    axios
      .put(
        `${API_URL}/api/classschedule/${id}`,
        { ...row, RecurrenceRule: rruleString },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => setSuccess("Updated"))
      .catch(() => setError("Failed to update"));

    setEditId(null);
  };

  const handleDelete = (id) => {
    axios
      .delete(`${API_URL}/api/classschedule/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setSuccess("Deleted"))
      .catch(() => setError("Failed to delete"));
  };

  // selection helpers
  const allSelected = data.length > 0 && data.every((r) => selectedRows.includes(r.ScheduleID));
  const isRowSelected = (id) => selectedRows.includes(id);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedRows(data.map((r) => r.ScheduleID));
    else setSelectedRows([]);
  };

  const handleSelectRow = (id) => (e) => {
    if (e.target.checked) setSelectedRows((prev) => [...prev, id]);
    else setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
  };

  const handleBulkActiveUpdate = (makeActive) => {
    const promises = selectedRows.map((id) => {
      const row = data.find((r) => r.ScheduleID === id);
      if (!row) return Promise.resolve();
      return axios.put(
        `${API_URL}/api/classschedule/${id}`,
        { ...row, IsActive: makeActive ? 1 : 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    });

    Promise.all(promises)
      .then(() => {
        setSuccess("Updated selected rows");
        setSelectedRows([]);
      })
      .catch(() => setError("Failed bulk update"));
  };

  // =========================
  // NEW: Add schedule submit
  // =========================
  const canAdd =
    addForm.SubCampusCode &&
    addForm.DegreeCode &&
    addForm.BranchCode &&
    addForm.Semester &&
    addForm.Regulation &&
    addForm.SubjectCode &&
    addForm.Section &&
    addForm.FromDate &&
    addForm.DayOfWeek &&
    addForm.StartHHmm &&
    Number(addForm.DurationMinutes) > 0;

  const handleAddSchedule = async () => {
    try {
      setError("");
      setBulkResult(null);

      await axios.post(
        `${API_URL}/api/classscheduleadmin`,
        {
          SubCampusCode: addForm.SubCampusCode,
          DegreeCode: addForm.DegreeCode,
          BranchCode: addForm.BranchCode,
          Semester: addForm.Semester,
          Regulation: addForm.Regulation,
          SubjectCode: addForm.SubjectCode,
          Section: addForm.Section,
          FromDate: addForm.FromDate,
          DayOfWeek: addForm.DayOfWeek,
          StartHHmm: addForm.StartHHmm,
          DurationMinutes: Number(addForm.DurationMinutes),
          UntilDate: addForm.UntilDate || "",
          IntervalWeeks: Number(addForm.IntervalWeeks || 1),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccess("Schedule created");
    } catch (e) {
      const msg = e?.response?.data?.error || "Failed to create schedule";
      setError(msg);
    }
  };

  // =========================
  // NEW: Bulk upload submit
  // =========================
  const handleBulkClick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setError("");
      setBulkResult(null);

      const form = new FormData();
      form.append("excelFile", file);

      const resp = await axios.post(`${API_URL}/api/classschedule/bulk-upload`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      setBulkResult(resp.data);
      setSuccess(`Bulk upload done. Inserted: ${resp.data.inserted}, Failed: ${resp.data.failed}`);
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      e.target.value = ""; // ✅ IMPORTANT (same as your working page)
    }
  };

  {/*
  const handleBulkUpload = async () => {
    if (!bulkFile) return;

    try {
      setError("");
      setBulkResult(null);

      const fd = new FormData();
      fd.append("excelFile", bulkFile);

      const resp = await axios.post(`${API_URL}/api/classschedule/bulk-upload`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setBulkResult(resp.data);
      setSuccess(`Bulk upload done. Inserted: ${resp.data.inserted}, Failed: ${resp.data.failed}`);
      setBulkFile(null);
    } catch (e) {
      const msg = e?.response?.data?.error || "Bulk upload failed";
      setError(msg);
    }
  };
  */}

  if (loading)
    return (
      <Box sx={{ ...pageWrapSx, display: "grid", placeItems: "center", minHeight: "40vh" }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Class Schedule
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Add schedules (via cascading class selection), bulk upload, edit recurrence, and bulk enable/disable
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={downloadSampleExcel}>
            Download Sample
            </Button>

            <Button variant="contained" sx={primaryBtnSx} startIcon={<UploadIcon />} onClick={handleBulkClick}>
              Bulk Upload
            </Button>

            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFile}
            />

            <Button
              variant="contained"
              sx={primaryBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(true)}
            >
              Set Bulk Active
            </Button>
            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              disabled={selectedRows.length === 0}
              onClick={() => handleBulkActiveUpdate(false)}
            >
              Set Bulk Inactive
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                mt: 2,
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Snackbar open autoHideDuration={2500} onClose={() => setSuccess("")}>
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            </Snackbar>
          )}

          {/* ✅ Existing Filters section */}
          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>Filters</Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                }}
              >
                {[
                  { label: "SubCampus", opts: opts.subs, val: fSub, setVal: setFSub },
                  { label: "Degree", opts: opts.degs, val: fDeg, setVal: setFDeg },
                  { label: "Branch", opts: opts.brs, val: fBr, setVal: setFBr },
                  { label: "Semester", opts: opts.sems, val: fSem, setVal: setFSem },
                  { label: "SubjectCode", opts: opts.subsj, val: fSubjCode, setVal: setFSubjCode },
                ].map(({ label, opts, val, setVal }) => (
                  <Autocomplete
                    key={label}
                    multiple
                    options={opts}
                    value={val}
                    onChange={(_, v) => setVal(v)}
                    renderInput={(p) => <TextField {...p} label={label} size="small" sx={inputSx} />}
                  />
                ))}
              </Box>
            </Box>
          </Paper>

          {/* ✅ NEW: Add Schedule */}
          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Add Class Schedule
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              {/* cascading selection */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(7, 1fr)" },
                  gap: 1,
                }}
              >
                <Autocomplete
                  options={addOptions.subs}
                  value={addForm.SubCampusCode || null}
                  onChange={(_, v) => onPickSub(v)}
                  renderInput={(p) => <TextField {...p} label="SubCampus" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  options={addOptions.degs}
                  value={addForm.DegreeCode || null}
                  onChange={(_, v) => onPickDeg(v)}
                  renderInput={(p) => <TextField {...p} label="Degree" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  options={addOptions.brs}
                  value={addForm.BranchCode || null}
                  onChange={(_, v) => onPickBr(v)}
                  renderInput={(p) => <TextField {...p} label="Branch" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  options={addOptions.sems}
                  value={addForm.Semester || null}
                  onChange={(_, v) => onPickSem(v)}
                  renderInput={(p) => <TextField {...p} label="Semester" size="small" sx={inputSx} />}
                />
                <Autocomplete
                  options={addOptions.regs}
                  value={addForm.Regulation || null}
                  onChange={(_, v) => onPickReg(v)}
                  renderInput={(p) => <TextField {...p} label="Regulation" size="small" sx={inputSx} />}
                />
                {/*
                <Autocomplete
                  options={addOptions.subjCodes}
                  value={addForm.SubjectCode || null}
                  onChange={(_, v) => onPickSubj(v)}
                  renderInput={(p) => <TextField {...p} label="SubjectCode" size="small" sx={inputSx} />}
                />
                */}
                <Autocomplete
                  options={addOptions.subjCodes} // array of objects
                  value={addOptions.subjCodes.find((s) => s.SubjectCode === addForm.SubjectCode) || null}
                  onChange={(_, v) => {
                    // IMPORTANT: store only string SubjectCode
                    setAdd({ SubjectCode: v?.SubjectCode || "", Section: "" });
                  }}
                  getOptionLabel={(opt) => opt?.label || ""}
                  renderInput={(p) => (
                    <TextField {...p} label="Subject (Code - Name)" size="small" sx={inputSx} />
                  )}
                />


                <Autocomplete
                  options={addOptions.sections}
                  value={addForm.Section || null}
                  onChange={(_, v) => onPickSection(v)}
                  renderInput={(p) => <TextField {...p} label="Section" size="small" sx={inputSx} />}
                />
              </Box>

              <Box sx={{ mt: 1, pl: 0.5 }}>
                <Typography sx={{ fontSize: "0.85rem", opacity: 0.85 }}>
                  Selected subject: <b>{addOptions.subjectName || "-"}</b>
                </Typography>
              </Box>

              {/* schedule fields - easy */}
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(6, 1fr)" },
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <TextField
                  label="From Date"
                  type="date"
                  size="small"
                  value={addForm.FromDate}
                  onChange={(e) => setAdd({ FromDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
                <TextField
                  label="Day"
                  size="small"
                  select
                  value={addForm.DayOfWeek}
                  onChange={(e) => setAdd({ DayOfWeek: e.target.value })}
                  sx={inputSx}
                  SelectProps={{ native: true }}
                >
                  {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </TextField>
                <TextField
                  label="Start Time (HH:mm)"
                  type="time"
                  size="small"
                  value={addForm.StartHHmm}
                  onChange={(e) => setAdd({ StartHHmm: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
                <TextField
                  label="Duration (min)"
                  type="number"
                  size="small"
                  value={addForm.DurationMinutes}
                  onChange={(e) => setAdd({ DurationMinutes: e.target.value })}
                  sx={inputSx}
                  inputProps={{ min: 1, max: 600 }}
                />
                <TextField
                  label="Until Date (optional)"
                  type="date"
                  size="small"
                  value={addForm.UntilDate}
                  onChange={(e) => setAdd({ UntilDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={inputSx}
                />
                <TextField
                  label="Interval (weeks)"
                  type="number"
                  size="small"
                  value={addForm.IntervalWeeks}
                  onChange={(e) => setAdd({ IntervalWeeks: e.target.value })}
                  sx={inputSx}
                  inputProps={{ min: 1, max: 52 }}
                />
              </Box>

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  sx={primaryBtnSx}
                  startIcon={<AddCircleIcon />}
                  disabled={!canAdd}
                  onClick={handleAddSchedule}
                >
                  Add Schedule
                </Button>

                <Typography sx={{ fontSize: "0.85rem", opacity: 0.8, alignSelf: "center" }}>
                  Backend will resolve <b>ClassID</b> and build RRULE automatically (IST).
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* ✅ NEW: Bulk Upload */}
          {/*
          <Paper variant="outlined" sx={{ ...sectionSx, mt: 2 }}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Bulk Upload (Excel)
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Button variant="outlined" sx={outlinedBtnSx} component="label" startIcon={<UploadFileIcon />}>
                  Choose Excel (.xlsx)
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setBulkFile(f);
                    }}
                  />
                </Button>

                <Button
                  variant="contained"
                  sx={primaryBtnSx}
                  disabled={!bulkFile}
                  onClick={handleBulkUpload}
                >
                  Upload & Import
                </Button>

                <Typography sx={{ fontSize: "0.85rem", opacity: 0.8 }}>
                  Sheet name must be <b>ClassSchedule</b>. Columns: SubCampusCode, DegreeCode, BranchCode, Semester,
                  Regulation, SubjectCode, Section, FromDate, DayOfWeek, StartTime(HH:mm), DurationMinutes, UntilDate,
                  IntervalWeeks.
                </Typography>
              </Box>

              {bulkFile && (
                <Typography sx={{ mt: 1, fontSize: "0.85rem" }}>
                  Selected file: <b>{bulkFile.name}</b>
                </Typography>
              )}

              {bulkResult?.ok && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    Inserted: <b>{bulkResult.inserted}</b> | Failed: <b>{bulkResult.failed}</b>
                  </Alert>
                  {Array.isArray(bulkResult.errors) && bulkResult.errors.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>Sample errors (first 50):</Typography>
                      <Box
                        sx={{
                          p: 1,
                          border: `1px solid ${BORDER}`,
                          borderRadius: 2,
                          background: "rgba(25,118,210,0.03)",
                          maxHeight: 180,
                          overflow: "auto",
                          fontSize: "0.85rem",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {bulkResult.errors.map((x, idx) => (
                          <div key={idx}>
                            Row {x.rowNo}: {x.error}
                          </div>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
          */}

          {/* ✅ Table */}
          <Paper elevation={0} sx={tableWrapSx}>
            <Box sx={{ overflowX: "auto", overflowY: "hidden" }}>
              <TableContainer component="div">
                <Table size="small">
                  <colgroup>
                    <col style={{ width: 50 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 170 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 320 }} />
                    <col style={{ width: 70 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 50 }} />
                  </colgroup>

                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell>#</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Section</TableCell>
                      <TableCell>Start</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Rule</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Actions</TableCell>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={selectedRows.length > 0 && !allSelected}
                          onChange={handleSelectAll}
                          sx={{
                            ...checkboxSx,
                            color: "rgba(255,255,255,0.95)",
                            "&.Mui-checked": { color: "#fff" },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.map((r, i) =>
                      editId === r.ScheduleID ? (
                        <TableRow key={r.ScheduleID} sx={zebraRowSx(i)}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{r.SubjectNameCode}</TableCell>
                          <TableCell>{r.Section}</TableCell>

                          <TableCell>
                            <TextField
                              name="StartTime"
                              type="datetime-local"
                              value={moment(r.StartTime).format("YYYY-MM-DDTHH:mm")}
                              onChange={(e) =>
                                handleInput(
                                  {
                                    target: {
                                      name: "StartTime",
                                      value: new Date(e.target.value).toISOString(),
                                    },
                                  },
                                  r.ScheduleID
                                )
                              }
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              sx={inputSx}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              name="DurationMinutes"
                              type="number"
                              value={r.DurationMinutes}
                              onChange={(e) =>
                                handleInput(
                                  {
                                    target: {
                                      name: "DurationMinutes",
                                      value: Number(e.target.value),
                                    },
                                  },
                                  r.ScheduleID
                                )
                              }
                              size="small"
                              label="Duration (min)"
                              InputLabelProps={{ shrink: true }}
                              fullWidth
                              sx={inputSx}
                              inputProps={{ min: 1, max: 600 }}
                            />
                          </TableCell>

                          <TableCell>
                            Every {r.RuleInterval} {(r.RuleFreq || "WEEKLY").toLowerCase()}
                            {r.RuleUntil ? ` until ${r.RuleUntil}` : ""}
                          </TableCell>

                          <TableCell>
                            <Checkbox
                              name="IsActive"
                              checked={r.IsActive === 1}
                              onChange={(e) => handleInput(e, r.ScheduleID)}
                              sx={checkboxSx}
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton sx={iconBtnSx} onClick={() => handleSave(r.ScheduleID)} title="Save">
                              <SaveIcon />
                            </IconButton>
                            <IconButton sx={iconBtnSx} onClick={() => setEditId(null)} title="Cancel">
                              <CancelIcon />
                            </IconButton>
                          </TableCell>

                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isRowSelected(r.ScheduleID)}
                              onChange={handleSelectRow(r.ScheduleID)}
                              sx={checkboxSx}
                            />
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={r.ScheduleID} sx={zebraRowSx(i)}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>{r.SubjectNameCode}</TableCell>
                          <TableCell>{r.Section}</TableCell>

                          <TableCell>{moment(r.StartTime).tz(moment.tz.guess()).format("YYYY-MM-DD HH:mm")}</TableCell>

                          <TableCell>{r.DurationMinutes} min</TableCell>

                          <TableCell>
                            {(() => {
                              const freq = (r.RuleFreq || "").toUpperCase();
                              const label =
                                freq === "DAILY"
                                  ? "day"
                                  : freq === "WEEKLY"
                                  ? "week"
                                  : freq === "MONTHLY"
                                  ? "month"
                                  : freq.toLowerCase();
                              return `Every ${r.RuleInterval} ${label}${r.RuleInterval > 1 ? "s" : ""}${
                                r.RuleUntil ? ` until ${r.RuleUntil}` : ""
                              }`;
                            })()}
                          </TableCell>

                          <TableCell>
                            <Checkbox checked={r.IsActive === 1} readOnly sx={checkboxSx} />
                          </TableCell>

                          <TableCell>
                            <IconButton sx={iconBtnSx} onClick={() => setEditId(r.ScheduleID)} title="Edit">
                              <EditIcon />
                            </IconButton>
                            <IconButton sx={iconBtnSx} onClick={() => handleDelete(r.ScheduleID)} title="Delete">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>

                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isRowSelected(r.ScheduleID)}
                              onChange={handleSelectRow(r.ScheduleID)}
                              sx={checkboxSx}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>
        </Box>
      </Paper>
    </Box>
  );
}
